import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  ScrollView,
  StatusBar,
  StyleSheet,
  RefreshControl,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { MyListing, UserMarketplaceStats } from '@/types/residents/marketplaceListing';
import { FeedbackModal } from '@/components/residents/marketplace';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import {
  useGetMyProfileQuery,
  useGetMyListingsQuery,
  useGetReviewsQuery,
} from '@/store/api';
import {
  GradientBackground,
  PageHeader,
  ProfileContent,
} from '@/components/residents/redesign';

type MarketplaceProfileNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceProfile'>;
type MarketplaceProfileRouteProp = RouteProp<RootStackParamList, 'MarketplaceProfile'>;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

const DEFAULT_STATS: UserMarketplaceStats = {
  bought: 0,
  sold: 0,
  followers: 0,
  following: 0,
  rating: 0,
  reviewCount: 0,
};

export const MarketplaceProfileScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopProfileView />;
  return <MobileProfileView />;
};

// =============================================================================
// Shared hook for profile data
// =============================================================================

function useProfileData() {
  const navigation = useNavigation<MarketplaceProfileNavigationProp>();
  const route = useRoute<MarketplaceProfileRouteProp>();

  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);

  const {
    data: userStats = DEFAULT_STATS,
  } = useGetMyProfileQuery();

  const {
    data: myListings = [],
    isLoading: isLoadingListings,
    refetch: refetchListings,
  } = useGetMyListingsQuery({ status: 'all' });

  const {
    data: feedbackItems = [],
  } = useGetReviewsQuery({});

  useEffect(() => {
    const params = route.params;
    if (params?.action && params?.listingId) {
      refetchListings();
      navigation.setParams({ action: undefined, listingId: undefined });
    }
  }, [route.params, refetchListings, navigation]);

  const handleListingPress = useCallback((listing: MyListing) => {
    navigation.navigate('MyListingItemDetail', { listing });
  }, [navigation]);

  const handleFollowingPress = useCallback(() => {
    navigation.navigate('MarketplaceFollowing');
  }, [navigation]);

  const handleRatingPress = useCallback(() => {
    setFeedbackModalVisible(true);
  }, []);

  const handleCloseFeedbackModal = useCallback(() => {
    setFeedbackModalVisible(false);
  }, []);

  return {
    userStats,
    myListings,
    isLoadingListings,
    feedbackItems,
    feedbackModalVisible,
    refetchListings,
    handleListingPress,
    handleFollowingPress,
    handleRatingPress,
    handleCloseFeedbackModal,
  };
}

// =============================================================================
// Desktop View
// =============================================================================

const DesktopProfileView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const {
    userStats,
    myListings,
    isLoadingListings,
    feedbackItems,
    feedbackModalVisible,
    refetchListings,
    handleListingPress,
    handleFollowingPress,
    handleRatingPress,
    handleCloseFeedbackModal,
  } = useProfileData();

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetchListings();
    setRefreshing(false);
  }, [refetchListings]);
  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <>
      <ScrollView
        style={styles.desktopContainer}
        contentContainerStyle={styles.desktopScrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.text.primary}
          />
        }
      >
        <View style={styles.desktopCentered}>
          <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
          <PageHeader title="My Profile" />
          <ProfileContent
            stats={userStats}
            listings={myListings}
            isLoadingListings={isLoadingListings}
            onListingPress={handleListingPress}
            onFollowingPress={handleFollowingPress}
            onRatingPress={handleRatingPress}
          />
        </View>
      </ScrollView>

      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={handleCloseFeedbackModal}
        feedbackItems={feedbackItems}
      />
    </>
  );
};

// =============================================================================
// Mobile View
// =============================================================================

const MobileProfileView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation();
  const {
    userStats,
    myListings,
    isLoadingListings,
    feedbackItems,
    feedbackModalVisible,
    refetchListings,
    handleListingPress,
    handleFollowingPress,
    handleRatingPress,
    handleCloseFeedbackModal,
  } = useProfileData();

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetchListings();
    setRefreshing(false);
  }, [refetchListings]);

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="Marketplace"
        darkBackground={true}
        headerVariant="gradient"
      >
        <ScrollView
          style={styles.mobileScroll}
          contentContainerStyle={styles.mobileScrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.text.primary}
            />
          }
        >
          <BackLink label="Back to marketplace" onPress={handleBack} color={PRIMITIVE_COLORS.resioBlue} />
          <PageHeader title="My Profile" />
          <ProfileContent
            stats={userStats}
            listings={myListings}
            isLoadingListings={isLoadingListings}
            onListingPress={handleListingPress}
            onFollowingPress={handleFollowingPress}
            onRatingPress={handleRatingPress}
          />
        </ScrollView>
      </BaseResidentMobileScreen>

      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={handleCloseFeedbackModal}
        feedbackItems={feedbackItems}
      />
    </GradientBackground>
  );
};

// =============================================================================
// Styles
// =============================================================================

const styles = StyleSheet.create({
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  desktopScrollContent: {
    padding: HP,
    paddingBottom: 60,
    alignItems: 'center',
  },
  desktopCentered: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    gap: 8,
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    paddingHorizontal: HP,
    paddingBottom: 40,
    gap: 8,
  },
});
